import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { getApiErrorMessage } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";

// ── Types ──────────────────────────────────────────────────────────────────

export interface ToxEntry {
  value: number;
  tox_level: 0.0 | 0.2 | 0.4 | 0.6 | 0.8;
  unit?: string;
}

export interface ChemtoxCompound {
  smiles: string;
  name: string;
  structure_svg: string;
}

export interface ChemtoxToxicity {
  acute_toxicity: {
    ld50: ToxEntry;
  };
  organ_toxicity: {
    herg: ToxEntry;
    dili: ToxEntry;
    skin_reaction: ToxEntry;
  };
  mutagenicity: {
    ames: ToxEntry;
    carcinogens: ToxEntry;
  };
  nuclear_receptor: Record<string, ToxEntry>;
  stress_receptor: Record<string, ToxEntry>;
  pharmacokinetics: {
    metabolism: Record<string, ToxEntry>;
    excretion: {
      half_life: ToxEntry;
      clearance_hepatocyte: ToxEntry;
    };
  };
}

export interface ChemtoxVisualizations {
  herg_attention_svg: string;
  dili_subgraph_svg: string;
  dili_subgraph_type: string;
  dili_total_subgraphs: number;
}

export interface DrugToxicityResult {
  compound: ChemtoxCompound;
  toxicity: ChemtoxToxicity;
  side_effects: Record<string, ToxEntry>;
  visualizations: ChemtoxVisualizations;
}

// AnimalInference(ToxBridge) 결과: fatetox와 달리 endpoint별 flat 값만 있다.
export interface AnimalToxicityResult {
  compound: {
    smiles: string;
    name: string;
  };
  endpoints: Record<string, number>;
  unit: string;
}

export interface iDrugToxicityState {
  result: DrugToxicityResult | AnimalToxicityResult | null;
  appendix: Record<string, any> | null;
  isLoading: boolean;
  isAppendixLoading: boolean;
  error: string | null;
}

interface ChemtoxParams {
  group_name: string;
  library_name: string;
  round?: number;
  use_backup?: boolean;
  timestamp?: string | null;
}

// ── Async Thunks ───────────────────────────────────────────────────────────

export const getDrugToxicityResultAPI = createAsyncThunk(
  "chemtox/getResult",
  async (params: ChemtoxParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(
        `${djangoEndpoint}/workflow/chemtox/result/`,
        { round: 1, ...params },
        { withCredentials: true }
      );
      return response.data as DrugToxicityResult | AnimalToxicityResult;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to load chemtox result")
      );
    }
  }
);

export const getDrugToxicityAppendixAPI = createAsyncThunk(
  "chemtox/getAppendix",
  async (params: ChemtoxParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(
        `${djangoEndpoint}/workflow/chemtox/appendix/`,
        { round: 1, ...params },
        { withCredentials: true }
      );
      return response.data as Record<string, any>;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to load appendix")
      );
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────────

const initialState: iDrugToxicityState = {
  result: null,
  appendix: null,
  isLoading: false,
  isAppendixLoading: false,
  error: null,
};

const chemtoxSlice = createSlice({
  name: "drugToxicity",
  initialState,
  reducers: {
    clearDrugToxicityResult: (state) => {
      state.result = null;
      state.appendix = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDrugToxicityResultAPI.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDrugToxicityResultAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.result = action.payload;
      })
      .addCase(getDrugToxicityResultAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getDrugToxicityAppendixAPI.pending, (state) => {
        state.isAppendixLoading = true;
      })
      .addCase(getDrugToxicityAppendixAPI.fulfilled, (state, action) => {
        state.isAppendixLoading = false;
        state.appendix = action.payload;
      })
      .addCase(getDrugToxicityAppendixAPI.rejected, (state) => {
        state.isAppendixLoading = false;
      });
  },
});

export const { clearDrugToxicityResult } = chemtoxSlice.actions;
export default chemtoxSlice.reducer;
